import { Auth, Hub } from 'aws-amplify';
import { useState, useEffect } from 'react';
import { validateToken } from '@utils/jwtValidate';
import awsmobile from '../../../aws-exports';

/**
 * Reads the access token directly from Amplify's own Cognito storage keys.
 * This is synchronous and always reflects the token Amplify is actually using,
 * eliminating the need for a separate copy in localStorage.
 */
export const getAmplifyToken = (): string | null => {
  const clientId = awsmobile.aws_user_pools_web_client_id;
  const lastUser = localStorage.getItem(
    `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`
  );
  if (!lastUser) return null;
  return localStorage.getItem(
    `CognitoIdentityServiceProvider.${clientId}.${lastUser}.accessToken`
  );
};

/**
 * Cognito's ways of saying the refresh token itself will never work again, as
 * opposed to "the request did not get through". Anything else is transient.
 */
const TERMINAL_REFRESH_ERRORS = new Set([
  'NotAuthorizedException',
  'UserNotFoundException'
]);

const isRefreshTokenRejected = (error: unknown): boolean => {
  const { code, name } =
    (error as { code?: string; name?: string } | null) ?? {};
  return (
    (typeof code === 'string' && TERMINAL_REFRESH_ERRORS.has(code)) ||
    (typeof name === 'string' && TERMINAL_REFRESH_ERRORS.has(name))
  );
};

/**
 * Derives authentication state from Amplify directly:
 * - Initial value is read synchronously from Amplify's own Cognito storage.
 * - Subsequent updates are driven by Amplify Hub auth events, so state stays
 *   in sync without polling or maintaining a separate localStorage token.
 */
export const useAuthListener = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() =>
    validateToken(getAmplifyToken())
  );
  const [isLoading, setIsLoading] = useState<boolean>(
    () => !validateToken(getAmplifyToken())
  );

  useEffect(() => {
    // If the access token is expired on mount, attempt a silent refresh via the
    // refresh token before deciding the user is unauthenticated. This covers the
    // cold-start case where the computer was off longer than the access token TTL.
    if (!validateToken(getAmplifyToken())) {
      Auth.currentSession()
        .catch(() => setIsAuthenticated(false))
        .finally(() => setIsLoading(false));
    }

    return Hub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'signIn':
        case 'tokenRefresh':
        case 'autoSignIn':
          setIsAuthenticated(true);
          break;
        case 'signOut':
          setIsAuthenticated(false);
          break;
        case 'tokenRefresh_failure':
          // Amplify dispatches this for ANY refreshSession error, and hands the
          // error itself as the payload's `data`. Only a refresh token Cognito
          // actually rejected ends the session: no network, DNS, a 5xx all mean
          // we could not ask, not that the answer was no. Treating those as
          // terminal sends a recoverable session to AuthWrapper, which clears
          // localStorage and takes the 365-day refresh token with it — over an
          // outage that may have lasted seconds. Waking a laptop before the
          // Wi-Fi reassociates is enough to trigger it.
          //
          // Staying put is safe both ways. Every request re-attempts
          // `Auth.currentSession()` (utils/getSession.ts), so connectivity
          // returning heals this by itself; and a session that really is dead is
          // still cleaned up by the 401 interceptor in api/client.ts, which
          // needs a server response and so only fires once we can tell.
          if (isRefreshTokenRejected(data)) {
            setIsAuthenticated(false);
          }
          break;
      }
    });
  }, []);

  return { isAuthenticated, isLoading };
};
